import React, { useEffect, useState, useRef } from 'react';
import { Dropdown } from '@/components/ui/dropdown';
import axios from '../utils/axiosInstance';
import { Button } from '@/components/ui/button';
import { PageLoader } from '@/components/ui/page-loader';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Card } from '@/components/ui/card';
import { Cropper } from '@/components/ui/image-crop';
import { Upload, RefreshCw, Trash2, Award } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { BASE_URL } from '@/config/constants';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';

interface AwardAsset {
  _id: string;
  name: string;
  image: string;
  category: 'Trophy' | 'Certificate' | 'Medal' | 'Badge' | 'Award' | 'Custom';
  description: string;
  isActive: boolean;
  createdAt: string;
}

const emptyAsset: Omit<AwardAsset, '_id' | 'createdAt'> = {
  name: '',
  image: '',
  category: 'Award',
  description: '',
  isActive: true
};

const CATEGORY_COLORS: Record<string, { bg: string; text: string }> = {
  'Trophy': { bg: 'bg-warning-light', text: 'text-warning' },
  'Certificate': { bg: 'bg-info-light', text: 'text-info' },
  'Medal': { bg: 'bg-info-light', text: 'text-info' },
  'Badge': { bg: 'bg-success-light', text: 'text-primary' },
  'Award': { bg: 'bg-danger-light', text: 'text-danger' },
  'Custom': { bg: 'bg-secondary', text: 'text-foreground' },
};

const AwardAssetManager: React.FC = () => {
  const [assets, setAssets] = useState<AwardAsset[]>([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [form, setForm] = useState(emptyAsset);
  const [editId, setEditId] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadStep, setUploadStep] = useState<'idle' | 'selecting' | 'cropping'>('idle');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const cropperRef = useRef<{ applyCrop: () => Promise<void> }>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  const fetchAssets = () => {
    setLoading(true);
    axios.get(`${BASE_URL}/api/award-assets/all`)
      .then(res => setAssets(res.data))
      .catch(err => {
        console.error('Error fetching assets:', err);
        toast.error('Failed to load assets');
        setAssets([]);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchAssets();
  }, []);

  const resetUploadState = () => {
    setUploadStep('idle');
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const openFileUpload = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    fileInputRef.current?.click();
  };

  const openCropperForExisting = () => {
    if (form.image) {
      fetch(form.image)
        .then(res => res.blob())
        .then(blob => {
          const file = new File([blob], 'asset-image.png', { type: blob.type });
          setSelectedFile(file);
          setUploadStep('cropping');
        })
        .catch(() => {
          toast.error('Failed to load image for editing');
        });
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validFormats = ['image/png', 'image/jpeg'];
    const maxSize = 2 * 1024 * 1024;

    if (!validFormats.includes(file.type)) {
      setError('Only .png and .jpeg files are allowed.');
      toast.error('Only .png and .jpeg files are allowed.');
      return;
    }

    if (file.size > maxSize) {
      setError('File size must be less than 2MB.');
      toast.error('File size must be less than 2MB.');
      return;
    }

    setError('');
    setSelectedFile(file);
    setUploadStep('cropping');
  };

  const handleCropComplete = async (croppedImage: string) => {
    if (!croppedImage) return;

    setUploading(true);
    try {
      const response = await fetch(croppedImage);
      const blob = await response.blob();

      const formData = new FormData();
      formData.append('file', blob, 'cropped-asset.png');

      const res = await axios.post(BASE_URL + '/api/images/upload', formData);
      setForm((prev) => ({ ...prev, image: res.data.url }));
      resetUploadState();
      const isUpdate = form.image ? true : false;
      toast.success(isUpdate ? 'Image updated successfully!' : 'Image uploaded successfully!');
    } catch (err) {
      setError('Image upload failed.');
      toast.error('Image upload failed.');
    } finally {
      setUploading(false);
    }
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target as HTMLInputElement;
    const newValue = type === 'checkbox' ? (e.target as HTMLInputElement).checked : value;
    setForm(prev => ({ ...prev, [name]: newValue }));
  };

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!form.name || !form.image) {
      setError('Name and image are required.');
      toast.error('Name and image are required.');
      return;
    }

    setSaving(true);
    try {
      const response = await axios.post(`${BASE_URL}/api/award-assets`, form);
      setAddOpen(false);
      setForm(emptyAsset);
      resetUploadState();
      toast.success('Asset added successfully!');
      const savedData = response.data?.data || response.data;
      if (savedData?._id) {
        setAssets(prev => [...prev, savedData]);
      } else {
        fetchAssets();
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to add asset';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (item: AwardAsset) => {
    setEditId(item._id);
    setForm({
      name: item.name,
      image: item.image,
      category: item.category,
      description: item.description,
      isActive: item.isActive
    });
    setEditOpen(true);
    setError('');
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!form.name || !form.image) {
      setError('Name and image are required.');
      toast.error('Name and image are required.');
      return;
    }

    setSaving(true);
    const currentEditId = editId;
    try {
      const response = await axios.put(`${BASE_URL}/api/award-assets/${editId}`, form);
      setEditOpen(false);
      setEditId(null);
      setForm(emptyAsset);
      resetUploadState();
      toast.success('Asset updated successfully!');
      const savedData = response.data?.data || response.data;
      if (savedData?._id) {
        setAssets(prev => prev.map(asset =>
          asset._id === currentEditId ? savedData : asset
        ));
      } else {
        fetchAssets();
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to update asset';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    const deletedAsset = assets.find(asset => asset._id === deleteId);
    // Optimistic removal
    setAssets(prev => prev.filter(asset => asset._id !== deleteId));
    setDeleteId(null);
    try {
      await axios.delete(`${BASE_URL}/api/award-assets/${deleteId}`);
      toast.success('Asset deleted successfully!');
    } catch (err: any) {
      // Revert on error
      if (deletedAsset) {
        setAssets(prev => [...prev, deletedAsset]);
      }
      toast.error(err.response?.data?.message || 'Failed to delete asset');
    }
  };

  const handleToggleActive = async (id: string, currentStatus: boolean) => {
    setTogglingId(id);
    // Optimistic update
    setAssets(prev => prev.map(asset =>
      asset._id === id ? { ...asset, isActive: !currentStatus } : asset
    ));
    try {
      await axios.patch(`${BASE_URL}/api/award-assets/${id}/toggle`);
      toast.success(`Asset ${currentStatus ? 'deactivated' : 'activated'} successfully!`);
    } catch (err: any) {
      // Revert on error
      setAssets(prev => prev.map(asset =>
        asset._id === id ? { ...asset, isActive: currentStatus } : asset
      ));
      toast.error(err.response?.data?.message || 'Failed to toggle asset status');
    } finally {
      setTogglingId(null);
    }
  };

  const renderForm = (onSubmit: (e: React.FormEvent) => Promise<void>) => (
    <form onSubmit={onSubmit} className="space-y-5">
      <div className="form-group">
        <Label className="form-label">Asset Name *</Label>
        <Input
          name="name"
          placeholder="e.g., Golden Trophy, Excellence Medal"
          value={form.name}
          onChange={handleFormChange}
          required
        />
      </div>

      <div className="form-group">
        <Label className="form-label">Category</Label>
        <div className="relative">
          <Dropdown
            options={(['Trophy', 'Certificate', 'Medal', 'Badge', 'Award', 'Custom'] as const).map(c => ({ value: c, label: c }))}
            value={form.category}
            onValueChange={(val) => setForm(prev => ({ ...prev, category: val as typeof form.category }))}
            placeholder="Select Category"
          />
        </div>
      </div>

      <div className="form-group">
        <Label className="form-label">Description</Label>
        <Textarea
          name="description"
          placeholder="Optional description"
          value={form.description}
          onChange={handleFormChange}
          rows={2}
        />
      </div>

      <div className="form-group">
        <Label className="form-label">Asset Image *</Label>
        {uploadStep === 'idle' ? (
          !form.image ? (
            <div className="w-full">
              <Card
                className="group flex max-h-[200px] w-full flex-col items-center justify-center gap-4 py-8 border-dashed text-sm cursor-pointer hover:bg-muted/50 transition-colors"
                onDragOver={(e: React.DragEvent<HTMLDivElement>) => {
                  e.preventDefault();
                  e.currentTarget.classList.add('bg-muted/50');
                }}
                onDragLeave={(e: React.DragEvent<HTMLDivElement>) => {
                  e.currentTarget.classList.remove('bg-muted/50');
                }}
                onDrop={(e: React.DragEvent<HTMLDivElement>) => {
                  e.preventDefault();
                  e.currentTarget.classList.remove('bg-muted/50');
                  const files = e.dataTransfer.files;
                  if (files.length > 0) {
                    const event = {
                      target: { files }
                    } as any;
                    handleFileChange(event);
                  }
                }}
                onClick={openFileUpload}
              >
                <div className="grid space-y-3">
                  <div className="flex items-center gap-x-2 text-muted-foreground justify-center">
                    <Upload className="size-5" />
                    <div>
                      Drop image here or{' '}
                      <Button
                        type="button"
                        variant="link"
                        className="text-primary p-0 h-auto font-normal"
                        onClick={(e) => {
                          e.stopPropagation();
                          openFileUpload();
                        }}
                      >
                        browse
                      </Button>{' '}
                      to upload
                    </div>
                  </div>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  accept=".png,.jpg,.jpeg"
                  onChange={handleFileChange}
                />
                <span className="text-xs text-muted-foreground mt-1 block">
                  Supported: JPG, PNG (max 2 MB)
                </span>
              </Card>
            </div>
          ) : (
            <div className="space-y-2">
              <Card
                className="group flex max-h-[400px] w-full flex-col items-center justify-center gap-4 py-8 border-dashed text-sm cursor-pointer hover:bg-muted/50 transition-colors"
                onClick={openCropperForExisting}
              >
                <div
                  className="relative max-h-64 w-full sm:max-w-64 rounded overflow-hidden"
                  style={{
                    backgroundImage:
                      'linear-gradient(45deg, #e5e7eb 25%, transparent 25%, transparent 75%, #e5e7eb 75%, #e5e7eb), linear-gradient(45deg, #e5e7eb 25%, transparent 25%, transparent 75%, #e5e7eb 75%, #e5e7eb)',
                    backgroundSize: '16px 16px',
                    backgroundPosition: '0 0, 8px 8px',
                    backgroundRepeat: 'repeat'
                  }}
                >
                  <img
                    src={form.image}
                    alt="uploaded"
                    className="w-full h-full object-cover cursor-pointer hover:opacity-80 transition-opacity"
                    title="Click to edit image"
                  />
                </div>
                <div className="flex items-center gap-x-2 text-muted-foreground text-xs">
                  <Upload className="size-4" />
                  <span>Click to edit or re-upload</span>
                </div>
              </Card>
              <div className="flex gap-2">
                <Button 
                  type="button" 
                  variant="ghost" 
                  size="sm" 
                  className="text-muted-foreground hover:text-foreground"
                  onClick={openFileUpload}
                >
                  <RefreshCw className="size-3.5" />
                  Change
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="text-muted-foreground hover:text-danger"
                  onClick={() => setForm(prev => ({ ...prev, image: '' }))}
                >
                  <Trash2 className="size-3.5" />
                  Remove
                </Button>
              </div>
              <input
                type="file"
                accept=".png,.jpg,.jpeg"
                onChange={handleFileChange}
                ref={fileInputRef}
                className="hidden"
              />
            </div>
          )
        ) : uploadStep === 'cropping' && selectedFile ? (
          <div className="space-y-2">
            <Card className="flex flex-col items-center">
              <div className="w-full border-b p-3">
                <p className="text-sm font-medium text-foreground text-center">Adjust crop area - Drag to move, edges to resize</p>
              </div>
              <div className="w-full flex justify-center py-3">
                <Cropper
                  file={selectedFile}
                  aspect={1}
                  maxImageSize={2 * 1024 * 1024}
                  onCrop={handleCropComplete}
                  onCropRef={cropperRef}
                />
              </div>
            </Card>
            <div className="flex gap-2">
              <Button
                type="button"
                onClick={async () => {
                  await cropperRef.current?.applyCrop();
                }}
                disabled={uploading}
                size="sm"
                loading={uploading}
                loadingText="Uploading..."
              >
                {form.image ? 'Update' : 'Upload'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => resetUploadState()}
                disabled={uploading}
                size="sm"
              >
                Cancel
              </Button>
            </div>
          </div>
        ) : null}
      </div>

      <div className="flex items-center space-x-3">
        <Switch
          checked={form.isActive}
          onCheckedChange={(checked) => setForm(prev => ({ ...prev, isActive: checked }))}
          aria-label="Toggle active state"
        />
        <span className={form.isActive ? 'status-label-active' : 'status-label-inactive'}>
          {form.isActive ? 'Active' : 'Inactive'}
        </span>
      </div>

      {error && <div className="text-danger text-sm">{error}</div>}
      <Button type="submit" loading={saving} loadingText={editId ? 'Updating...' : 'Adding...'}>
        {editId ? 'Update' : 'Add'} Asset
      </Button>
    </form>
  );

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
        <h2 className="page-title">Award Asset Library</h2>
        <Dialog open={addOpen} onOpenChange={setAddOpen}>
          <DialogTrigger asChild>
            <Button>Add Asset</Button>
          </DialogTrigger>
          <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Add New Asset</DialogTitle>
              <DialogDescription>Create a new award asset with image, name, category and description</DialogDescription>
            </DialogHeader>
            {renderForm(handleAdd)}
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-card rounded-lg border p-4 mb-6">
        <p className="text-sm text-muted-foreground">
          Manage high-quality award assets here. Domain admins will select from this library when creating awards, ensuring consistent and professional appearance across all domains.
        </p>
      </div>

      <div>
        {loading ? (
          <PageLoader label="Loading assets..." />
        ) : assets.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">
              <Award className="w-full h-full" />
            </div>
            <h3 className="empty-state-title">No assets found</h3>
            <p className="empty-state-description">Create your first award asset to get started</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead>Image</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-24">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {assets.map(asset => (
                <TableRow 
                  key={asset._id} 
                  className={`hover:bg-muted/50 transition-colors ${asset.isActive ? 'bg-card' : 'bg-muted/50'}`}
                >
                  <TableCell className="align-middle">
                    {asset.image && (
                      <img src={asset.image} alt={asset.name} className="w-16 h-16 object-cover rounded" />
                    )}
                  </TableCell>
                  <TableCell className={`font-medium align-middle ${asset.isActive ? 'text-foreground' : 'text-muted-foreground'}`}>
                    {asset.name}
                  </TableCell>
                  <TableCell className={`text-sm align-middle ${asset.isActive ? 'text-foreground' : 'text-muted-foreground'}`}>
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                      CATEGORY_COLORS[asset.category]?.bg || 'bg-secondary'
                    } ${CATEGORY_COLORS[asset.category]?.text || 'text-foreground'}`}>
                      {asset.category}
                    </span>
                  </TableCell>
                  <TableCell className={`text-sm align-middle ${asset.isActive ? 'text-muted-foreground' : 'text-muted-foreground'} max-w-xs truncate`}>
                    {asset.description || '-'}
                  </TableCell>
                  <TableCell className="align-middle">
                    <div className="flex items-center space-x-2">
                      <Switch
                        checked={asset.isActive}
                        onCheckedChange={() => handleToggleActive(asset._id, asset.isActive)}
                        aria-label="Toggle asset active state"
                        disabled={togglingId === asset._id}
                      />
                      <span className={asset.isActive ? 'status-label-active' : 'status-label-inactive'}>
                        {asset.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="w-24">
                    <div className="flex gap-2 items-center justify-center h-full">
                      <Button 
                        variant="outline" 
                        size="icon-sm" 
                        onClick={() => handleEdit(asset)}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                        </svg>
                      </Button>
                      <Button 
                        variant="destructive" 
                        size="icon-sm" 
                        onClick={() => setDeleteId(asset._id)}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="3 6 5 6 21 6"></polyline>
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                          <line x1="10" y1="11" x2="10" y2="17"></line>
                          <line x1="14" y1="11" x2="14" y2="17"></line>
                        </svg>
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Asset</DialogTitle>
            <DialogDescription>Update the asset details including image, name, category and description</DialogDescription>
          </DialogHeader>
          {renderForm(handleEditSubmit)}
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!deleteId}
        onOpenChange={(open) => !open && setDeleteId(null)}
        title="Delete Asset"
        description="Are you sure you want to delete this asset? This action cannot be undone."
        confirmLabel="Delete"
        onConfirm={handleDelete}
      />
    </div>
  );
};

export default AwardAssetManager;
